import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

// See api/DTOs/RecordDto.cs (RecordPlantingSuggestionDto).
export interface PlantingSuggestion {
  canAdd: boolean;
  hasPlanting: boolean;
  locationName?: string;
  timePeriodName?: string;
  plantingDate?: string; // yyyy-mm-dd
  areaHectares?: number;
  areaSource?: string;
  irrigationTypeName?: string;
  rateKgPerHa?: number;
  rateUnitId?: string;
  geneStackId?: string;
  geneStackName?: string;
  varieties: { id: string; name: string; geneStackId?: string; geneStackName?: string; plantedNearby: boolean }[];
  suggestedProductId?: string;
}

type Variety = PlantingSuggestion['varieties'][number];

interface Props {
  open: boolean;
  // The record whose location and season the planting goes against.
  recordId: string | null;
  authHeaders: () => { Authorization: string };
  onClose: () => void;
  // The added planting record, described for a confirmation.
  onAdded: (summary: string) => void;
}

const formatDay = (isoDate: string) =>
  new Date(`${isoDate}T00:00:00Z`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

// Adds the planting record a field is missing for a season (e.g. for
// Validation's "Defoliation with no planting"), at the record's own
// location and season: 1 Nov, the field's hectares, and 12 kg/ha irrigated
// or 6 kg/ha dryland, all editable. The admin picks the variety. The server
// checks again that there's still no planting record before adding it
// (POST api/records/{id}/planting).
const AddPlantingDialog: React.FC<Props> = ({ open, recordId, authHeaders, onClose, onAdded }) => {
  const [suggestion, setSuggestion] = useState<PlantingSuggestion | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [varietyId, setVarietyId] = useState<string | null>(null);
  const [date, setDate] = useState('');
  const [area, setArea] = useState('');
  const [rate, setRate] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open || !recordId) return;
    let cancelled = false;
    setSuggestion(null);
    setError(null);
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`/api/records/${recordId}/planting?includeVarieties=true`, { headers: authHeaders() });
        if (!res.ok) throw new Error('Failed to load the planting defaults');
        const s: PlantingSuggestion = await res.json();
        if (cancelled) return;
        setSuggestion(s);
        const nearby = s.varieties.filter((v) => v.plantedNearby);
        setVarietyId(s.suggestedProductId ?? (nearby.length === 1 ? nearby[0].id : null));
        setDate(s.plantingDate ?? '');
        setArea(s.areaHectares != null ? String(Math.round(s.areaHectares * 100) / 100) : '');
        setRate(s.rateKgPerHa != null ? String(s.rateKgPerHa) : '');
        setNotes(`Added as the missing planting record for ${s.timePeriodName ?? 'this season'}.`);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, recordId, authHeaders]);

  // Planted on this farm that season first, then the record's gene stack,
  // then the rest.
  const options = useMemo(() => {
    if (!suggestion) return [];
    const rank = (v: Variety) => (v.plantedNearby ? 0 : suggestion.geneStackId && v.geneStackId === suggestion.geneStackId ? 1 : 2);
    return [...suggestion.varieties].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  }, [suggestion]);
  const groupOf = (v: Variety) =>
    v.plantedNearby
      ? `Already on this farm in ${suggestion?.timePeriodName ?? 'this season'}`
      : suggestion?.geneStackId && v.geneStackId === suggestion.geneStackId
        ? `${suggestion.geneStackName} (this record's gene stack)`
        : 'Other varieties';

  const variety = options.find((v) => v.id === varietyId) ?? null;
  const rateValid = Number(rate) > 0;
  const areaValid = area.trim() === '' || Number(area) > 0;

  const handleAdd = async () => {
    if (!recordId || !suggestion?.rateUnitId || !variety) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/records/${recordId}/planting`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          productId: variety.id,
          plantingDate: date,
          areaHectares: area.trim() === '' ? null : Number(area),
          rate: Number(rate),
          rateUnitId: suggestion.rateUnitId,
          notes,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to add the planting record');
      }
      onAdded(`${variety.name} on ${formatDay(date)}, ${rate} kg/ha${area.trim() ? ` over ${area} ha` : ''}`);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const rateHelp = suggestion?.irrigationTypeName
    ? suggestion.rateKgPerHa != null
      ? `${suggestion.irrigationTypeName}, so ${suggestion.rateKgPerHa} kg/ha`
      : `${suggestion.irrigationTypeName}: no default rate`
    : 'Irrigation unknown: 12 kg/ha irrigated, 6 dryland';

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>
        Add planting record
        {suggestion && (
          <Typography variant="body2" color="text.secondary">
            {[suggestion.locationName, suggestion.timePeriodName].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {loading || !suggestion ? (
          !error && <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
        ) : !suggestion.canAdd ? (
          <Alert severity="info">
            {suggestion.hasPlanting
              ? `This field already has a planting record in ${suggestion.timePeriodName}.`
              : 'This record has no field or season to add a planting to.'}
          </Alert>
        ) : (
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Autocomplete
              options={options}
              groupBy={groupOf}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={variety}
              onChange={(_, v) => setVarietyId(v?.id ?? null)}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Box>
                    {o.name}
                    {o.geneStackName && <Typography variant="caption" color="text.secondary" display="block">{o.geneStackName}</Typography>}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Variety"
                  autoFocus
                  helperText={variety?.geneStackName ? `Gene stack: ${variety.geneStackName}` : 'A Planting Seed product'}
                />
              )}
            />
            <TextField
              type="date"
              label="Planting date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={suggestion.plantingDate ? `Default 1 Nov in ${suggestion.timePeriodName}` : undefined}
            />
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                type="number"
                label="Area (ha)"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                error={!areaValid}
                helperText={!areaValid ? 'Must be more than 0' : suggestion.areaSource ? `From ${suggestion.areaSource}` : 'Blank uses the mapped area'}
              />
              <TextField
                fullWidth
                type="number"
                label="Rate (kg/ha)"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                error={rate !== '' && !rateValid}
                helperText={rateHelp}
              />
            </Box>
            <TextField fullWidth multiline minRows={2} label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            {!suggestion.rateUnitId && <Alert severity="warning">There's no Kg/ha unit to record the rate in.</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={saving || loading || !suggestion?.canAdd || !variety || !date || !rateValid || !areaValid || !suggestion?.rateUnitId}
        >
          {saving ? 'Adding...' : 'Add planting'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPlantingDialog;
